import React, { useEffect } from 'react'
import { AuthProvider, useAuth } from 'react-oidc-context'

import { CognitoRefreshToken, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'

import { COGNITO_AUTHORITY, COGNITO_CLIENT_ID, COGNITO_POOL_ID } from '../utils/Constants'

const PUBLIC_PATHS = ['/signin', '/signed-out', '/forgot-password', '/reset-password']

const isPublicPath = pathname =>
  PUBLIC_PATHS.includes(pathname) || pathname.startsWith('/shared/file/') || pathname.startsWith('/review/')

const onSigninCallback = user => {
  window.history.replaceState({}, document.title, window.location.pathname)
}

export const userManager = new UserManager({
  authority: COGNITO_AUTHORITY,
  client_id: COGNITO_CLIENT_ID,
  redirect_uri: window.location.origin,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: false,
})

const Content = props => {
  const auth = useAuth()

  // Proactively refresh the access token ~1 minute before it expires.
  //
  // We use amazon-cognito-identity-js refreshSession() directly instead of
  // auth.signinSilent() because signinSilent() falls back to a hidden iframe
  // pointed at the app's redirect_uri (the root URL). That iframe loads the
  // full React app, which also registers this handler and fires signinSilent(),
  // which opens another iframe — an exponential cascade that freezes the tab.
  //
  // refreshSession() posts directly to the Cognito API (no iframe, no loaded URL).
  // We then write the new tokens back into oidc-client-ts storage manually so
  // react-oidc-context picks them up via the userLoaded event.
  //
  // auth.userManager is a stable singleton — empty deps [] is correct.
  useEffect(() => {
    const checkAndRefresh = async () => {
      try {
        const currentUser = await userManager.getUser()
        if (!currentUser || !currentUser.access_token) return

        const expiresAt = currentUser.expires_at
        const now = Date.now() / 1000

        // Proactively refresh the access token if it expires in less than 5 minutes (300 seconds)
        if (expiresAt && expiresAt - now < 300) {
          if (window.isRefreshingSession) {
            console.log('[Auth] Token refresh already in progress elsewhere. Skipping.')
            return
          }
          window.isRefreshingSession = true
          console.log('[Auth] Token is close to expiring. Refreshing session...')

          const refreshTokenValue = currentUser.refresh_token
          const username =
            currentUser.profile?.['cognito:username'] || currentUser.profile?.sub || currentUser.profile?.email

          if (!refreshTokenValue || !username) {
            window.isRefreshingSession = false
            console.warn('[Auth] Stale session with missing refresh token or username.')
            await userManager.removeUser()
            window.location.replace('/signin')
            return
          }

          const userPool = new CognitoUserPool({
            UserPoolId: COGNITO_POOL_ID,
            ClientId: COGNITO_CLIENT_ID,
          })
          const cognitoUser = new CognitoUser({ Username: username, Pool: userPool })
          const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenValue })

          cognitoUser.refreshSession(refreshToken, async (err, session) => {
            window.isRefreshingSession = false
            if (err) {
              console.error('[Auth] Background session refresh failed:', err)
              const isTerminal =
                err.code === 'NotAuthorizedException' ||
                err.code === 'UserNotFoundException' ||
                err.code === 'UserNotConfirmedException' ||
                err.code === 'InvalidParameterException' ||
                err.code === 'invalid_grant' ||
                err.name === 'NotAuthorizedException' ||
                err.name === 'invalid_grant' ||
                (err.message &&
                  (err.message.includes('Refresh Token has expired') ||
                    err.message.includes('Token is expired') ||
                    err.message.includes('invalid_grant') ||
                    err.message.includes('invalid grant') ||
                    err.message.includes('revoked')))

              if (isTerminal) {
                console.warn('[Auth] Terminal authentication error during refresh. Redirecting to sign-in.', err)
                await userManager.removeUser()
                const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
                window.location.replace(`/signin?returnTo=${returnTo}`)
              }
              return
            }

            try {
              const updatedUser = new User({
                id_token: session.getIdToken().getJwtToken(),
                access_token: session.getAccessToken().getJwtToken(),
                refresh_token: session.getRefreshToken() ? session.getRefreshToken().getToken() : refreshTokenValue,
                token_type: currentUser.token_type || 'Bearer',
                scope: currentUser.scope || 'openid profile email',
                profile: session.getIdToken().payload,
                expires_at: session.getAccessToken().getExpiration(),
                state: currentUser.state,
              })

              // Persist the updated tokens back to oidc-client-ts storage
              await userManager.storeUser(updatedUser)
              // Notify the user manager events so the React state updates
              if (userManager.events._userLoaded) {
                userManager.events._userLoaded.raise(updatedUser)
              }
              console.log('[Auth] Background token refresh successful.')
            } catch (cbErr) {
              console.error('[Auth] Exception in background refresh callback:', cbErr)
            }
          })
        }
      } catch (err) {
        console.error('[Auth] Error during background session validation:', err)
      }
    }

    // Run check immediately on mount
    checkAndRefresh()

    // Check once every minute
    const interval = setInterval(checkAndRefresh, 60000)
    return () => clearInterval(interval)
  }, [])

  if (auth.isLoading) {
    return <div>Loading...</div>
  }

  // Public paths (sign-in, forgot-password, etc.) must be checked BEFORE the
  // auth.error and unauthenticated-redirect blocks below.
  //
  // The loop that was happening:
  //   1. Session expires → stale OIDC tokens remain in localStorage
  //   2. User (or redirect) lands on /signin
  //   3. oidc-client-ts re-initialises, finds the stale/expired state, sets auth.error
  //   4. The error block called removeUser() + window.location.href = '/signin'
  //   5. Full page reload → back to step 3 → infinite loop
  //
  // Fix: if we're already on a public path, skip both the error-redirect and the
  // unauthenticated-redirect. We still clear stale state via removeUser() so the
  // next sign-in attempt starts clean — but we do NOT navigate away.
  if (isPublicPath(window.location.pathname)) {
    if (auth.error) {
      // Clean up stale OIDC state silently; stay on the sign-in page
      auth.removeUser()
    }
    return props.children
  }

  if (auth.error) {
    if (auth.error.message === 'No end session endpoint') {
      window.location.replace('https://google.com')
      return null
    }

    // Any other auth error on a protected path — clear stale state and redirect.
    auth.removeUser()
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.replace(`/signin?returnTo=${returnTo}`)
    return null
  }

  if (auth.isAuthenticated) {
    return props.children
  }

  // Not authenticated on a protected path — redirect to our custom sign-in page
  // (do not use auth.signinRedirect() which goes to Cognito's hosted UI)
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.replace(`/signin?returnTo=${returnTo}`)
  return null
}

export default props => {
  return (
    <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
      <Content>{props.children}</Content>
    </AuthProvider>
  )
}
